/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, EDoc, EDocDetailsDialogConfig, edocDetailsDialogRoute, isJobEditable, JobDetailsModel } from '@gh/builder-shared';
import { BuilderPermission, EDocType } from '@gh/builder-shared-data';
import { EnumDictionary, getRouteInitData, MutablePageState, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { Enum } from '@gh/core-data';
import { catchErrors, mapMessageParams } from '@gh/core-messages';
import { translationTuple } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { throwError$ } from '@gh/rx';
import { filter$, switchMap$ } from '@gh/rx/operators';
import {
  JobDetailsTabBusinessService,
  JobEDocOwnerSet,
  JobEDocSigningDocument,
} from '../../../resources/job-details-tab';
import { JobEDocDetails } from '../../../resources/job-details-tab/job-details-tab-model';

@Component({
  selector: 'gh-job-details-e-docs-tab',
  template: require('./job-details-e-docs-tab.component.html'),
})
export class JobDetailsEDocsTabComponent implements OnInit {
  jobId: ID;
  form: MutablePageState<JobEDocDetails>;

  private edocTypeDict: EnumDictionary<EDocType>;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private navigations: BuilderNavigationService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private business: JobDetailsTabBusinessService,
              private model: JobDetailsModel,
              private waitings: WaitingService) {
  }

  ngOnInit(): void {
    const { jobId, ownerSet, log, edocTypeDict } = getRouteInitData(this.route);
    this.jobId = jobId;
    this.edocTypeDict = edocTypeDict;

    this.form = new MutablePageState(
      this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT) && isJobEditable(this.model.basicInfo)
        ? FormMode.EditMode
        : FormMode.ViewMode,
      { ownerSet, log, logLoading: false });
  }

  onUpdateOwnerSet(owners: JobEDocOwnerSet): void {
    this.form.value.ownerSet = owners;
  }

  onOpenDocument(document: EDoc): void {
    this.dialogs.openWithInput(edocDetailsDialogRoute, <EDocDetailsDialogConfig>{
      documentId: document.id,
    });
  }

  onSendDocsToSign(docs: JobEDocSigningDocument[]): void {
    // send documents to sign
    this.business.sendDocumentsToSign(this.jobId, { jobId: this.jobId, docs, confirmations: {} }).pipe(
      this.waitings.operator(),
      catchErrors(
        [
          'msg_e_document_signed',
          (basket) => this.dialogs.confirmation(yesNoConfirmation(
            'msg_e_document_signed',
            // tslint:disable-next-line:no-non-null-assertion
            this.translateMessageParams(basket.get('msg_e_document_signed')!.params))).pipe(
            filter$(yesAnswer),
            // if user wants to sign new documents => run sign process again without duplicate check
            switchMap$(() => this.business.sendDocumentsToSign(this.jobId, {
              jobId: this.jobId,
              docs,
              confirmations: { skipDuplicateSendCheck: true },
            }).pipe(this.waitings.operator()))),
        ],
        [
          'msg_e_document_pending_signature',
          (basket) => throwError$(basket.map((error) => mapMessageParams(
            error,
            (params) => this.translateMessageParams(params)))),
        ],
      ))
      .subscribe(
        () => {
          this.notifications.success('msg_e_doc_added_successfully');
          this.reloadLog();
        },
        this.notifications.processAll);
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  reloadLog(): void {
    const finishLoading = () => this.form.value.logLoading;

    this.form.value.logLoading = true;
    this.business.getEDocLog(this.jobId)
      .subscribe(
        (log) => {
          finishLoading();
          this.form.value.log = log;
        },
        finishLoading,
        finishLoading);
  }

  private translateMessageParams(params: Hash<any>): Hash<any> {
    return {
      ...params,
      types: params.types
        ? params.types.map((type: string) => this.edocTypeDict.get(Enum.byValue(EDocType, type))).join(', ')
        : void 0,
    };
  }
}
